import { IconCopy, IconEdit, IconHistoryToggle, IconTransitionTop } from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';
import { DiffViewer } from './DiffViewer';
import { CopyButton } from './Buttons';

export type DiffData = {
  showDiff: boolean;
  firstData: string;
  secondData: string;
};

export const HighlightedDatablockData = ({
  prompt,
  diffData,
}: {
  prompt: string;
  diffData?: DiffData;
}) => {
  const parts = prompt.split(/(\{\{psvar\.[^}]+\}\})/g); // split and keep the match
  return (
    <div>
      <pre className="w-full max-h-72 overflow-y-auto border border-dashed border-slate-300 p-5 rounded-lg bg-slate-50 whitespace-pre-wrap break-words">
        {parts.map((part, idx) => {
          const isMatch = part.match(/^\{\{psvar\.[^}]+\}\}$/);
          return isMatch ? (
            <span key={idx} className="text-[#CA7842] font-semibold">
              {part}
            </span>
          ) : (
            <span key={idx}>{part}</span>
          );
        })}
      </pre>
      {diffData && diffData.showDiff ? (
        <DiffViewer
          firstData={diffData.firstData}
          secondData={diffData.secondData}
          className="mt-5"
        />
      ) : null}
    </div>
  );
};

type DatablockPreviewActions = {
  onVersionClick: () => void;
  showActions: boolean;
};

type StandardPromptProps = {
  name: string;
  envKey: string;
  datablockKey: string;
  role: string;
  prompt: string;
  className?: string;
  actions?: DatablockPreviewActions;
  diffData?: DiffData;
};

export const StandardPromptPreview: React.FC<StandardPromptProps> = ({
  className = '',
  name,
  envKey,
  datablockKey,
  role,
  prompt,
  actions,
  diffData,
}) => {
  return (
    <div className={`w-full p-8 bg-white shadow-sm rounded-xl flex flex-row ${className}`}>
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-row justify-between w-full items-center">
          <div className="flex flex-row items-center">
            <span>{name}</span>
            <CopyButton data={datablockKey} title="Copy datablock key" filled />
          </div>
          {actions && actions.showActions ? (
            <div className="flex flex-row">
              <IconTransitionTop
                size="1.5rem"
                className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                title="Copy version to another environment"
              />
              <NavLink to={`environment/${envKey}/datablock/${datablockKey}/edit`}>
                <IconEdit
                  size="1.5rem"
                  className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                  title="Edit datablock "
                />
              </NavLink>
              <IconHistoryToggle
                size="1.5rem"
                className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                title="View previous versions"
                onClick={actions.onVersionClick}
              />
            </div>
          ) : (
            <div />
          )}
        </div>
        <div className="flex flex-row h-[40px] w-max rounded-lg">
          <div className="rounded-tl-lg rounded-bl-lg bg-[#ff9148] text-white p-[8px] h-full w-max">
            Role
          </div>
          <div className="rounded-tr-lg border border-dashed border-slate-300 rounded-br-lg bg-white p-[6px] h-full w-max">
            {role}
          </div>
        </div>
        <HighlightedDatablockData prompt={prompt} diffData={diffData} />
      </div>
    </div>
  );
};

type VariableDataPromptProps = {
  name: string;
  envKey: string;
  datablockKey: string;
  variable: string;
  className?: string;
  actions?: DatablockPreviewActions;
  diffData?: DiffData;
};

export const VariableDataPromptPreview: React.FC<VariableDataPromptProps> = ({
  className = '',
  name,
  envKey,
  datablockKey,
  variable,
  actions,
  diffData,
}) => {
  return (
    <div className={`w-full p-8 bg-white shadow-sm rounded-xl flex flex-row ${className}`}>
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-row justify-between w-full items-center">
          <div className="flex flex-row items-center">
            <span>{name}</span>
            <CopyButton data={datablockKey} title="Copy datablock key" filled />
          </div>
          {actions && actions.showActions ? (
            <div className="flex flex-row">
              <IconTransitionTop
                size="1.5rem"
                className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                title="Copy version to another environment"
              />
              <NavLink to={`environment/${envKey}/datablock/${datablockKey}/edit`}>
                <IconEdit
                  size="1.5rem"
                  className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                  title="Edit datablock "
                />
              </NavLink>
              <IconHistoryToggle
                size="1.5rem"
                className="ml-3 text-slate-500 hover:text-slate-800 cursor-pointer"
                title="View previous versions"
                onClick={actions.onVersionClick}
              />
            </div>
          ) : (
            <div />
          )}
        </div>
        <pre className="w-full max-h-72 overflow-y-auto rounded-lg bg-slate-50 whitespace-pre-wrap break-words">
          <HighlightedDatablockData prompt={variable} diffData={diffData} />
        </pre>
      </div>
    </div>
  );
};
